import type { EventOutcomesResponse } from '../../models/entities/event-outcomes.model';
import type { AiFeedbackResponse } from '../../models/entities/ai-feedback.model';
import type { CameraReportResponse } from '../../models/entities/camera-report.model';
import type { EventActionResponse } from '../../models/entities/event-action.model';
import type { EventHandle, EventHandlesResponse } from '../../models/entities/event-handle.model';
import type { AttachAlarmClipResponse } from '../../models/entities/alarm-clip.model';
import type { SnapshotResponse } from '../../models/entities/snapshot.model';
import type {
  EventAlarmsResponse,
  EventCamerasResponse,
  EventDetail,
  EventTalkdownsResponse,
} from '../../models/entities/event-detail.model';
import type {
  CameraDetectionsResponse,
  EventCameraStreamResponse,
  EventPlaybackClipDownload,
} from '../../models/entities/event-playback.model';
import type { EventsInProcessResponse } from '../../models/entities/events-in-process.model';
import type {
  EventsParkedResponse,
  ParkEventResponse,
} from '../../models/entities/events-parked.model';
import type { EventsQueueResponse } from '../../models/entities/events-queue.model';
import type { ApiEndpointDefinition } from '../api-endpoint.types';

export const EVENT_ENDPOINTS = {
  eventsParked: {
    id: 'events-parked',
    method: 'GET',
    apiPath: '/api/dealer/events/parked',
    page: 'events',
    entity: 'events-parked',
  } satisfies ApiEndpointDefinition<EventsParkedResponse>,
  eventHandles: {
    id: 'event-handles',
    method: 'GET',
    apiPath: '/api/dealer/events/handles',
    page: 'events',
    entity: 'event-handles',
  } satisfies ApiEndpointDefinition<EventHandlesResponse>,
  eventDetail: {
    id: 'event-detail',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId',
    page: 'events',
    entity: 'event-detail',
  } satisfies ApiEndpointDefinition<EventDetail>,
  eventAlarms: {
    id: 'event-alarms',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/alarms',
    page: 'events',
    entity: 'event-alarms',
  } satisfies ApiEndpointDefinition<EventAlarmsResponse>,
  eventCameraAlarms: {
    id: 'event-camera-alarms',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/cameras/:cameraId/alarms',
    page: 'events',
    entity: 'event-camera-alarms',
  } satisfies ApiEndpointDefinition<EventAlarmsResponse>,
  eventCameras: {
    id: 'event-cameras',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/cameras',
    page: 'events',
    entity: 'event-cameras',
  } satisfies ApiEndpointDefinition<EventCamerasResponse>,
  eventTalkdowns: {
    id: 'event-talkdowns',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/talkdowns',
    page: 'events',
    entity: 'event-talkdowns',
  } satisfies ApiEndpointDefinition<EventTalkdownsResponse>,
  eventRaiseToResolve: {
    id: 'event-raise-to-resolve',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/raise-to-resolve',
    page: 'events',
    entity: 'event-raise-to-resolve',
  } satisfies ApiEndpointDefinition<EventActionResponse>,
  eventPark: {
    id: 'event-park',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/park',
    page: 'events',
    entity: 'event-park',
  } satisfies ApiEndpointDefinition<ParkEventResponse>,
  /**
   * Binary clip export — mock interceptor returns `/media/sample_video_51s.mp4` as a blob.
   */
  eventPlaybackClip: {
    id: 'event-playback-clip',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/playback/clip',
    page: 'events',
    entity: 'event-playback-clip',
  } satisfies ApiEndpointDefinition<EventPlaybackClipDownload>,
  eventCameraStream: {
    id: 'event-camera-stream',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/cameras/:numericId/stream',
    page: 'events',
    entity: 'event-camera-stream',
  } satisfies ApiEndpointDefinition<EventCameraStreamResponse>,
  cameraDetections: {
    id: 'camera-detections',
    method: 'GET',
    apiPath: '/api/dealer/cameras/:numericId/detections',
    page: 'events',
    entity: 'camera-detections',
  } satisfies ApiEndpointDefinition<CameraDetectionsResponse>,
  eventCameraReport: {
    id: 'event-camera-report',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/cameras/:cameraId/report',
    page: 'events',
    entity: 'event-camera-report',
  } satisfies ApiEndpointDefinition<CameraReportResponse>,
  eventCameraSnapshot: {
    id: 'event-camera-snapshot',
    method: 'POST',
    apiPath: '/api/resolve/events/:eventId/assets',
    page: 'events',
    entity: 'event-camera-snapshot',
  } satisfies ApiEndpointDefinition<SnapshotResponse>,
  eventAlarmAttachClip: {
    id: 'event-alarm-attach-clip',
    method: 'POST',
    apiPath: '/api/resolve/events/:eventId/assets',
    page: 'events',
    entity: 'event-alarm-attach-clip',
  } satisfies ApiEndpointDefinition<AttachAlarmClipResponse>,
  eventDetectionFeedback: {
    id: 'event-detection-feedback',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/detections/:detectionId/feedback',
    page: 'events',
    entity: 'event-detection-feedback',
  } satisfies ApiEndpointDefinition<AiFeedbackResponse>,
} as const satisfies Record<string, ApiEndpointDefinition>;
